import { useState, useMemo } from 'react'
import { format } from 'date-fns'
import { useCustodyPayments, useCustodyExpenses } from '../hooks/useCustody'
import { useLinkedHouseholds } from '../../linkedHouseholds/hooks/useLinkedHouseholds'
import { useAuth } from '../../../hooks/useAuth'
import { Spinner } from '../../../components/ui/Spinner'
import { cn } from '../../../lib/utils'
import { Portal } from '../../../components/Portal'

const METHODS = [
  { id: 'venmo',  label: 'Venmo' },
  { id: 'zelle',  label: 'Zelle' },
  { id: 'cash',   label: 'Cash' },
  { id: 'check',  label: 'Check' },
  { id: 'other',  label: 'Other' },
]

export const RecordPaymentModal = ({ netBalance, onClose }) => {
  const { household }      = useAuth()
  const { activeLink }     = useLinkedHouseholds()
  const { create }         = useCustodyPayments()
  const { data: expenses = [] } = useCustodyExpenses()

  const linkedHouseholdName = activeLink?.otherHousehold?.name ?? 'Other House'

  // Unsettled expenses where other household owes us (they pay)
  // or where we owe them — both directions shown
  const unsettled = useMemo(() =>
    expenses.filter((e) => e.status !== 'settled'),
  [expenses])

  const [amount,     setAmount]     = useState(Math.abs(netBalance ?? 0).toFixed(2))
  const [date,       setDate]       = useState(format(new Date(), 'yyyy-MM-dd'))
  const [method,     setMethod]     = useState('')
  const [refNote,    setRefNote]    = useState('')
  const [notes,      setNotes]      = useState('')
  const [selected,   setSelected]   = useState(() => new Set(unsettled.map((e) => e.id)))
  const [saving,     setSaving]     = useState(false)
  const [error,      setError]      = useState('')

  const toggleExpense = (id) => {
    setSelected((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  const handleSave = async () => {
    const amt = parseFloat(amount)
    if (!amt || amt <= 0) { setError('Enter a valid amount.'); return }
    setSaving(true)
    setError('')
    try {
      await create.mutateAsync({
        amount:         amt,
        paymentDate:    date,
        paymentMethod:  method || null,
        referenceNote:  refNote.trim() || null,
        expenseIds:     Array.from(selected),
        notes:          notes.trim() || null,
      })
      onClose()
    } catch (err) {
      setError(err.message ?? 'Save failed.')
      setSaving(false)
    }
  }

  const inputCls = 'w-full rounded-xl border border-border px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30'
  const lbl = (t) => <label className="block text-xs font-semibold text-text-muted uppercase tracking-wide mb-1">{t}</label>

  return (
    <Portal>
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center pb-[env(safe-area-inset-bottom,0px)] sm:pb-0 overflow-hidden" onClick={onClose}>
      <div className="absolute inset-0 bg-black/40" />
      <div
        className="relative bg-surface rounded-t-2xl sm:rounded-2xl w-full max-w-lg max-h-[90dvh] flex flex-col shadow-card"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-5 pt-5 pb-3 border-b border-border flex-shrink-0">
          <h2 className="text-base font-bold text-text-primary">Record Payment</h2>
          <button type="button" onClick={onClose} className="text-text-muted p-1">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M18 6 6 18M6 6l12 12"/>
            </svg>
          </button>
        </div>

        <div className="overflow-y-auto flex-1 min-h-0 px-5 py-4 space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div>
              {lbl('Amount *')}
              <input
                type="number"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                min="0"
                step="0.01"
                className={inputCls}
              />
            </div>
            <div>
              {lbl('Date')}
              <input type="date" value={date} onChange={(e) => setDate(e.target.value)} className={inputCls} />
            </div>
          </div>

          <div>
            {lbl('Payment Method')}
            <div className="flex flex-wrap gap-2">
              {METHODS.map((m) => (
                <button
                  key={m.id}
                  type="button"
                  onClick={() => setMethod(method === m.id ? '' : m.id)}
                  className={cn(
                    'px-3 py-1.5 rounded-full text-sm font-medium border transition-colors',
                    method === m.id ? 'bg-primary text-white border-primary' : 'border-border text-text-secondary',
                  )}
                >
                  {m.label}
                </button>
              ))}
            </div>
          </div>

          <div>
            {lbl('Reference Note')}
            <input
              type="text"
              value={refNote}
              onChange={(e) => setRefNote(e.target.value)}
              placeholder="e.g. Venmo @username, check #1234"
              className={inputCls}
            />
          </div>

          {/* Expense selector */}
          {unsettled.length > 0 && (
            <div>
              {lbl('Covers These Expenses')}
              <div className="space-y-1 max-h-48 overflow-y-auto">
                {unsettled.map((e) => {
                  const owedAmt = e.split_type === 'fifty_fifty'
                    ? e.amount * 0.5
                    : e.amount * (e.split_percentage ?? 50) / 100
                  return (
                    <label
                      key={e.id}
                      className="flex items-center gap-3 bg-background rounded-xl px-4 py-2.5 cursor-pointer"
                    >
                      <input
                        type="checkbox"
                        checked={selected.has(e.id)}
                        onChange={() => toggleExpense(e.id)}
                        className="accent-primary"
                      />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-text-primary truncate">{e.description}</p>
                        <p className="text-xs text-text-muted">{e.child?.display_name} · ${owedAmt.toFixed(2)} owed</p>
                      </div>
                    </label>
                  )
                })}
              </div>
            </div>
          )}

          <div>
            {lbl('Notes')}
            <input
              type="text"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Optional notes…"
              className={inputCls}
            />
          </div>

          {error && <p className="text-sm text-rose-600">{error}</p>}
        </div>

        <div className="px-5 py-4 border-t border-border flex gap-2 flex-shrink-0">
          <button type="button" onClick={onClose}
            className="flex-1 border border-border rounded-xl py-3 text-sm font-medium text-text-secondary">
            Cancel
          </button>
          <button type="button" onClick={handleSave} disabled={saving}
            className="flex-1 bg-primary text-white rounded-xl py-3 text-sm font-semibold disabled:opacity-50 flex items-center justify-center gap-2">
            {saving ? <><Spinner size="sm" /> Saving…</> : 'Record Payment'}
          </button>
        </div>
      </div>
    </div>
    </Portal>
  )
}
